import { FileWithPath, useDropzone } from "react-dropzone";
import { ReactNode, useCallback, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { AddImage } from "../../api/UploadImage/UploadImg";

const Upload = ({
  folder,
  className,
  children,
  onUploadStarts = () => {},
  onUploadEnds = () => {},
  onFileUploaded = () => {},
  onUploadingChange = () => {},
  acceptedFiles = { "image/*": [] },
  isVideo = false,
  manual = false,
  onFileSelected = () => {},
  maxFiles,
}: {
  folder: string;
  className?: string;
  children: ReactNode;
  onUploadStarts?: () => void;
  onUploadEnds?: () => void;
  onFileUploaded?: (fileName: string, key?: string, duration?: string) => void;
  onFileSelected?: (file: File) => void;
  acceptedFiles?: { [fileType: string]: string[] };
  isVideo?: boolean;
  onUploadingChange?: (isUploading: boolean) => void;
  manual?: boolean;
  maxFiles?: number;
}) => {
  const [uploadProgress, setUploadProgress] = useState(0); // State to track upload progress

  const { mutate } = useMutation({
    mutationFn: (formData: FormData) => {
      return AddImage(
        formData,
        folder,
        (event) => {
          // Cap the progress at 95% unless the upload is confirmed to be successful
          const progress = Math.round((event.loaded * 90) / event.total);
          setUploadProgress(progress);
        },
        isVideo
      );
    },
    onSuccess: (response) => {
      onUploadEnds();
      onFileUploaded(response.url, response.key, response.duration);
      setUploadProgress(100);
    },
    onError: () => {
      onUploadEnds();
      setUploadProgress(0);
    },
  });
  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      onUploadStarts();
      onUploadingChange(true);
      acceptedFiles.map((file: FileWithPath) => {
        if (manual) {
          onFileSelected?.(file);
          onUploadingChange(false);
          onUploadEnds();
          return;
        }

        const reader = new FileReader();
        reader.onload = () => {
          const formData = new FormData();
          formData.append("file", file);
          mutate(formData, {
            onSettled: () => {
              onUploadingChange(false);
            },
          });
          onFileUploaded?.(file.name);
        };
        reader.readAsArrayBuffer(file);
      });
    },
    [
      mutate,
      onFileUploaded,
      onFileSelected,
      onUploadStarts,
      onUploadEnds,
      onUploadingChange,
      manual,
    ]
  );

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    accept: acceptedFiles,
    ...(maxFiles !== undefined ? { maxFiles } : {}),
  });

  return (
    <div className={className}>
      <div {...getRootProps({ className: "dropzone" })}>
        <input {...getInputProps()} />
        {children}
      </div>
      {uploadProgress > 0 && (
        <div style={{ width: "100%" }}>
          <div
            className="bg-[#4379EE] h-2"
            style={{ width: `${uploadProgress}%` }}
          ></div>
        </div>
      )}
    </div>
  );
};

export default Upload;
